<script lang="ts">
  import { getErrorMessage } from '$lib/api';
  import { Plus, Edit3, Trash2, Lightbulb, Power, Sun, Blinds, MapPin, X } from 'lucide-svelte';
  import { LOAD_TYPES } from '$lib/constants/load';
  import { loadsQuery } from '$lib/queries/loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { rcuChannelsQuery } from '$lib/queries/rcu_channels';
  import { deleteLoadMutation } from '$lib/mutations/loads';
  import { setLoading, setError, clearError, uiState } from '$lib/state/ui.svelte';
  import { showSuccess, showError } from '$lib/state/toast.svelte';
  import ConfirmDialog from './ui/ConfirmDialog.svelte';
  import SkeletonCard from './ui/SkeletonCard.svelte';
  import Modal from './ui/Modal.svelte';
  import LoadForm from './forms/LoadForm.svelte';
  import type { LoadResponse } from '$lib/types/load';
  import type { RoomResponse } from '$lib/types/room';

  const unitLoadsQ = loadsQuery();
  const unitRoomsQ = roomsQuery();
  const unitChannelsQ = rcuChannelsQuery();
  const deleteLoad = deleteLoadMutation();

  let unitRooms = $derived(unitRoomsQ.data ?? []);
  let unitLoads = $derived(unitLoadsQ.data ?? []);
  let isLoadsLoading = $derived(unitLoadsQ.isLoading);
  let mappedLoadIds = $derived(new Set((unitChannelsQ.data ?? []).map((c) => c.load)));

  let filterUnassignedOnly = $state(false);
  let filterLoadType = $state('');

  let filteredLoads = $derived(
    unitLoads.filter((load) => {
      if (filterUnassignedOnly && mappedLoadIds.has(load.id)) return false;
      if (filterLoadType && load.load_type !== filterLoadType) return false;
      return true;
    })
  );

  let loadTypeOptions = $derived([...new Set(unitLoads.map((l) => l.load_type))]);

  function getLoadsForRoom(roomId: number) {
    return filteredLoads.filter((load) => load.room === roomId);
  }

  let visibleRooms = $derived(
    unitRooms.filter((room) =>
      filterUnassignedOnly || filterLoadType ? getLoadsForRoom(room.id).length > 0 : true
    )
  );

  let showLoadDialog = $state(false);
  let editingLoad = $state<LoadResponse | null>(null);

  let showDeleteLoadConfirm = $state(false);
  let loadToDelete = $state<LoadResponse | null>(null);

  async function handleDeleteLoad() {
    showDeleteLoadConfirm = false;
    if (!loadToDelete) return;

    setLoading(true);
    clearError();

    try {
      await deleteLoad.mutateAsync(loadToDelete.id);
      showSuccess('Load deleted successfully');
    } catch (error: unknown) {
      showError(getErrorMessage(error));
    } finally {
      setLoading(false);
      loadToDelete = null;
    }
  }

  function confirmDeleteLoad(load: LoadResponse) {
    loadToDelete = load;
    showDeleteLoadConfirm = true;
  }

  function editLoad(load: LoadResponse) {
    editingLoad = load;
    uiState.createDialogData = {
      load: load,
      isEditing: true
    };
    showLoadDialog = true;
  }

  function getLoadTypeIcon(loadType: string) {
    switch (loadType) {
      case 'phase_cut':
        return Lightbulb;
      case 'relay':
        return Power;
      case '1-10v':
        return Sun;
      case 'curtain':
        return Blinds;
      case 'dali-dt6':
        return Lightbulb;
      case 'dali-dt8':
        return Lightbulb;
      default:
        return Lightbulb;
    }
  }
</script>

<div>
  <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
    <div>
      <h2 class="text-base font-medium text-text-primary">Browse all loads</h2>
      <p class="text-xs text-text-muted mt-0.5">
        {filteredLoads.length} load{filteredLoads.length !== 1 ? 's' : ''}
        {#if filterUnassignedOnly || filterLoadType}
          (filtered)
        {/if}
      </p>
    </div>
    <button
      type="button"
      onclick={() => {
        uiState.createDialogData = { load: {}, isEditing: false };
        showLoadDialog = true;
      }}
      class="btn-primary inline-flex items-center gap-1.5 text-sm"
    >
      <Plus class="w-3.5 h-3.5" />
      Add Load
    </button>
  </div>

  <div class="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-border-default">
    <label class="inline-flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
      <input
        type="checkbox"
        bind:checked={filterUnassignedOnly}
        class="rounded border-border-strong"
      />
      Unassigned only
    </label>
    <label class="inline-flex items-center gap-2 text-sm text-text-secondary">
      <span>By load type</span>
      <select
        bind:value={filterLoadType}
        class="text-sm border border-border-strong rounded px-2 py-1 bg-bg-canvas text-text-primary"
      >
        <option value="">All types</option>
        {#each loadTypeOptions as type}
          <option value={type}>{LOAD_TYPES[type]?.label ?? type}</option>
        {/each}
      </select>
    </label>
  </div>

  <div class="space-y-4">
    {#if visibleRooms.length > 0}
      {#each visibleRooms as room}
        {@const roomLoads = getLoadsForRoom(room.id)}
        <div class="border border-border-default rounded-lg">
          <div
            class="flex items-center justify-between p-4 bg-bg-rail border-b border-border-default"
          >
            <div class="flex items-center space-x-3">
              <MapPin class="w-5 h-5 text-blue-600" />
              <div>
                <h5 class="font-medium text-text-primary">{room.name}</h5>
                <!-- {#if room.description}
                  <p class="text-sm text-text-muted">{room.description}</p>
                {/if} -->
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-text-muted"
                >{roomLoads.length} load{roomLoads.length !== 1 ? 's' : ''}</span
              >
              <button
                onclick={() => {
                  uiState.createDialogData = { load: { room: room.id }, isEditing: false };
                  showLoadDialog = true;
                }}
                class="inline-flex items-center px-3 py-1.5 border border-border-strong text-xs font-medium rounded-md text-text-secondary bg-bg-canvas hover:bg-bg-rail focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary"
              >
                <Plus class="w-3 h-3 mr-1" />
                Add Load
              </button>
            </div>
          </div>

          <div class="p-4">
            {#if roomLoads.length > 0 || isLoadsLoading}
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {#if isLoadsLoading}
                  {#each Array(3) as _}
                    <SkeletonCard />
                  {/each}
                {:else}
                  {#each roomLoads as load}
                    <div
                      class="border border-border-default rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-colors"
                    >
                      <div class="flex items-start justify-between">
                        <div class="flex items-center space-x-3">
                          <svelte:component
                            this={getLoadTypeIcon(load.load_type)}
                            class="w-5 h-5 text-blue-600"
                          />
                          <div>
                            <h6 class="text-sm font-medium text-text-primary">
                              {load.name}
                            </h6>
                            <p class="text-xs text-text-muted">
                              {LOAD_TYPES[load.load_type]?.label}
                            </p>
                          </div>
                        </div>
                        <div class="flex items-center space-x-1">
                          <button
                            onclick={() => editLoad(load)}
                            class="p-1 text-text-muted hover:text-text-secondary rounded"
                            title="Edit load"
                            aria-label="Edit load"
                          >
                            <Edit3 class="w-3 h-3" />
                          </button>
                          <button
                            onclick={() => confirmDeleteLoad(load)}
                            class="p-1 text-text-muted hover:text-red-600 rounded"
                            title="Delete load"
                            aria-label="Delete load"
                          >
                            <Trash2 class="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  {/each}
                {/if}
              </div>
            {:else}
              <div class="text-center py-4 text-text-muted">
                <Lightbulb class="w-8 h-8 mx-auto mb-2 text-text-disabled" />
                <p class="text-sm">No loads in this room</p>
                <button
                  onclick={() => {
                    uiState.createDialogData = { load: { room: room.id }, isEditing: false };
                    showLoadDialog = true;
                  }}
                  class="mt-2 text-xs text-blue-600 hover:text-blue-800"
                >
                  Add first load
                </button>
              </div>
            {/if}
          </div>
        </div>
      {/each}
    {:else}
      <div class="text-center py-8">
        <MapPin class="w-12 h-12 text-text-disabled mx-auto mb-4" />
        <h5 class="text-sm font-medium text-text-primary mb-2">No rooms available</h5>
        <p class="text-sm text-text-muted mb-4">Create rooms first to add loads</p>
      </div>
    {/if}
  </div>

  {#if unitLoads.length > 0}
    <div class="mt-6 bg-bg-rail rounded-lg p-4">
      <h4 class="text-sm font-medium text-text-primary mb-3">Load Summary</h4>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        {#each Object.entries(LOAD_TYPES) as [type, config]}
          {@const typeCount = unitLoads.filter((l) => l.load_type === type).length}
          {#if typeCount > 0}
            <div class="flex items-center space-x-2">
              <svelte:component this={getLoadTypeIcon(type)} class="w-4 h-4 text-text-muted" />
              <span class="text-sm text-text-secondary">{config.label}</span>
              <span class="text-sm font-medium text-text-primary">{typeCount}</span>
            </div>
          {/if}
        {/each}
      </div>
      <div class="mt-3 pt-3 border-t border-border-default">
        <span class="text-sm font-medium text-text-primary">Total Loads: {unitLoads.length}</span>
      </div>
    </div>
  {/if}
</div>

<Modal
  bind:open={showLoadDialog}
  size="lg"
  onclose={() => {
    uiState.createDialogData = null;
    editingLoad = null;
  }}
>
  {#snippet header()}
    <div class="flex items-center justify-between w-full">
      <h2 class="text-xl font-semibold text-text-primary">
        {editingLoad ? 'Edit Load' : 'Create a New Load'}
      </h2>
      <button
        type="button"
        class="p-2 bg-bg-hover hover:bg-bg-hover rounded-lg transition"
        aria-label="Close"
        onclick={() => {
          showLoadDialog = false;
        }}
      >
        <X class="w-5 h-5" />
      </button>
    </div>
  {/snippet}
  {#snippet children()}
    <LoadForm
      onsuccess={() => (showLoadDialog = false)}
      oncancel={() => (showLoadDialog = false)}
    />
  {/snippet}
</Modal>

<ConfirmDialog
  bind:open={showDeleteLoadConfirm}
  title="Delete Load"
  message={`Are you sure you want to delete load "${loadToDelete?.name}"?`}
  confirmText="Delete"
  variant="danger"
  on:confirm={handleDeleteLoad}
/>
